import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
}                           from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  HaynesProVesaComponentGroupModel,
  HaynesProVesaComponentModel,
  HaynesProVesaSystemListItemModel
}                           from '../typings/haynes-pro.vesa';
import { Table }            from '../../../../table/types/table';
import { TableRowEvent }    from '../../../../table/types/table-row-event';
import { VesaService }      from '../vesa.service';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-vesa-components',
  styles: [ require('./haynes-pro.diagnostic-tool.vesa-components.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.vesa-components.component.html')
})
export class HaynesProDiagnosticToolVesaComponentsComponent implements OnInit {

  @Input() components : Array<HaynesProVesaComponentGroupModel>;
  @Input() selections : Array<{
    description?             : string;
    group_id                 : string;
    group_text               : string;
    source?                  : 'vesa' | 'comfort_electronics';
    system_type_id?          : string;
    wiring_diagram_system_id?: string;
  }>;
  @Input() vehicleId  : number;

  @Output() componentSelect = new EventEmitter<{
    component   : HaynesProVesaComponentModel;
    source?     : 'vesa' | 'comfort_electronics';
    systemTypeId: string;
  }>();

  componentTitle   = '';
  filterText       = '';
  isLoadingComfort = false;

  componentsTableSettings : Table;
  filteredComponents      : Array<HaynesProVesaComponentModel>;
  systemItems             : Array<HaynesProVesaSystemListItemModel>;

  private comfortLoadPromises : Map<string, Promise<void>> = new Map();

  constructor (
    private translateService : TranslateService,
    private vesaService      : VesaService
  ) {}

  ngOnInit () : void {
    this.buildSystemList();
    this.buildTableSettings();
  }

  handleComponentSelection ( component : HaynesProVesaComponentModel ) : void {
    const selectedSystem = this.systemItems?.find(s => s.selected);

    if (!selectedSystem) {
      return;
    }

    this.componentSelect.emit({
      component   : component,
      source      : selectedSystem.source,
      systemTypeId: selectedSystem.system_type_id
    });
  }

  handleSystemSelection ( system : HaynesProVesaSystemListItemModel ) : void {
    this.systemItems.forEach(s => s.selected = false);
    system.selected = true;

    if (system.source === 'comfort_electronics') {
      this.loadComfortComponents(system).then(() => this.buildComponentList());
      return;
    }

    this.buildComponentList();
  }

  onComponentRowClick = ( event : TableRowEvent ) : void => {
    this.handleComponentSelection(event.row);
  };

  onFilterChange () : void {
    this.buildComponentList();
  }

  private buildComponentList () : void {
    const selected = this.systemItems?.find(s => s.selected);

    if (!selected) {
      this.filteredComponents = [];
      this.componentTitle     = '';

      return;
    }

    const match = this.components.find(
      g => g.system_info.system_type_id === selected.system_type_id
    );

    if (!match) {
      this.filteredComponents = [];
      this.componentTitle     = '';

      return;
    }

    this.componentTitle = selected.label;

    const filter = (this.filterText || '').toLowerCase().trim();

    // Spread into a new array so wor-table's @Input() setter picks up the change
    this.filteredComponents = filter
      ? match.components.filter(c => c.name.toLowerCase().includes(filter))
      : [ ...match.components ];
  }

  private buildSystemList () : void {
    if (!this.components?.length) {
      return;
    }

    this.systemItems = this.components.map(group => {
      const source    = group.system_info.source || 'vesa';
      const selection = ( this.selections || [] ).find(s => {
        const selectionId = s.system_type_id || s.wiring_diagram_system_id;

        return selectionId === group.system_info.system_type_id;
      });
      const groupText = selection?.group_text || '';

      return {
        description   : group.system_info.system_description,
        groupText     : groupText,
        label         : groupText ? groupText + ' - ' + group.system_info.system_description : group.system_info.system_description,
        source        : source,
        system_type_id: group.system_info.system_type_id
      };
    });

    this.systemItems[0].selected = true;

    const firstSystem = this.systemItems[0];

    if (firstSystem.source === 'comfort_electronics') {
      this.loadComfortComponents(firstSystem).then(() => this.buildComponentList());
    } else {
      this.buildComponentList();
    }

    // prepare the cache for remaining comfort systems in parallel so clicking feels instant
    this.preloadComfortComponents();
  }

  private buildTableSettings () : void {
    this.componentsTableSettings = {
      columns: [
        {
          field : 'name',
          header: { text: '' }
        }
      ],
      empty: {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_RECORDS_FOUND', {
          type: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.COMPONENTS')
        })
      },
      rows: {
        highlight: { enabled: true },
        select   : { enabled: true, identifier: 'selected', multi: false }
      },
      sorting: { enabled: false }
    };
  }

  private loadComfortComponents ( system : HaynesProVesaSystemListItemModel ) : Promise<void> {
    const group = this.components.find(g => g.system_info.system_type_id === system.system_type_id);

    if (!group || group.components?.length || !this.vehicleId) {
      return Promise.resolve();
    }

    this.isLoadingComfort = true;

    return this.fetchComfortComponentsForGroup(group)
      .finally(() => {
        this.isLoadingComfort = false;
      });
  }

  private preloadComfortComponents () : void {
    if (!this.vehicleId) {
      return;
    }

    ( this.components || [] )
      .filter(g => g.system_info.source === 'comfort_electronics' && !g.components?.length)
      .forEach(g => {
        this.fetchComfortComponentsForGroup(g);
      });
  }

  private fetchComfortComponentsForGroup ( group : HaynesProVesaComponentGroupModel ) : Promise<void> {
    const systemId = group.system_info.system_type_id;

    // Dedupe: if this group is already being fetched, return the in-flight promise
    const existing = this.comfortLoadPromises.get(systemId);

    if (existing) {
      return existing;
    }

    const promise = this.vesaService.getWiringDiagramSchemes(this.vehicleId, systemId)
      .then(( schemes : any ) => {
        const mimeDataName = this.extractMimeDataName(schemes);

        group.system_info.mime_data_name = mimeDataName;

        if (!mimeDataName) {
          group.components = [];
          return;
        }

        return this.vesaService.getWiringComponents(this.vehicleId, mimeDataName)
          .then(( response : any ) => {
            group.components = this.normaliseWiringComponents(response);
          });
      })
      .catch(() => {
        // Silent on preload path. If this was a click-triggered load, loadComfortComponents still toggles isLoadingComfort off and the RHS renders the empty state naturally.
        group.components = [];
      })
      .finally(() => {
        this.comfortLoadPromises.delete(systemId);
      });

    this.comfortLoadPromises.set(systemId, promise);

    return promise;
  }

  private extractMimeDataName ( response : any ) : string {
    if (!response) {
      return '';
    }

    if (typeof response === 'string') {
      return response;
    }

    if (Array.isArray(response)) {
      return this.extractMimeDataName(response[0]);
    }

    return response.mime_data_name
      || response.mimeDataName
      || response.image_url
      || response.url
      || '';
  }

  private normaliseWiringComponents ( response : any ) : Array<HaynesProVesaComponentModel> {
    const rows = Array.isArray(response)
      ? response
      : ( response?.components || [] );

    return ( rows || [] ).map(( item : any ) => {
      const code        = ( item?.name || item?.code || '' ).toString().trim();
      const description = ( item?.description || '' ).toString().trim();

      const descriptionHasCode = !!code && (
        description === code
          || description.toLowerCase().startsWith(( code + ' ' ).toLowerCase())
          || description.toLowerCase().startsWith(( code + ':' ).toLowerCase())
      );

      const display = descriptionHasCode
        ? description
        : ( code && description ? `${code} ${description}` : ( description || code ) );

      return {
        id  : String(item?.id || item?.code || item?.name || item?.component_id || item?.symbol_instance_id || '').trim(),
        name: display.trim(),
        type: item?.type || ''
      };
    });
  }
}
